import { useEffect, useRef, useState } from 'react';
import { Shield, Users, Clock, BadgeCheck, Headphones, CreditCard, Map, Award } from 'lucide-react';
import { cn } from '../../lib/utils';

const features = [
  {
    icon: Shield,
    title: 'Best Prices Guaranteed',
    description: 'We offer competitive pricing with no hidden charges. Price match guarantee on all bookings.',
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50'
  },
  {
    icon: Users,
    title: 'Expert Local Guides',
    description: 'Our certified guides bring destinations to life with insider knowledge and authentic experiences.',
    color: 'bg-green-500',
    lightColor: 'bg-green-50'
  },
  {
    icon: Headphones,
    title: '24/7 Customer Support',
    description: 'Round-the-clock assistance for all your travel needs. We\'re always just a call away.',
    color: 'bg-purple-500',
    lightColor: 'bg-purple-50'
  },
  {
    icon: BadgeCheck,
    title: 'Verified Hotels',
    description: 'Every hotel is personally inspected and verified to ensure quality and comfort.',
    color: 'bg-orange-500',
    lightColor: 'bg-orange-50'
  }
];

const stats = [
  { value: '15K+', label: 'Happy Travelers' },
  { value: '500+', label: 'Tour Packages' },
  { value: '50+', label: 'Destinations' },
  { value: '98%', label: 'Satisfaction Rate' }
];

export function WhyChooseUs() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-[#FDFBF7] to-white" 
      data-testid="why-choose-us"
    >
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#FF6B35] text-sm font-semibold tracking-wider uppercase mb-3">
            🏆 Why Travel With Us
          </span>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Why Choose NYRAV Tours?
          </h2>
          <p className="text-[#4A4A4A] max-w-2xl mx-auto text-lg">
            With years of experience and thousands of happy travelers, we deliver 
            exceptional journeys that exceed expectations.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className={cn(
                'group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-500',
                'transform hover:-translate-y-2 border border-gray-100',
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              )}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Icon */}
              <div className={cn(
                'w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-all duration-300',
                feature.lightColor,
                'group-hover:scale-110'
              )}>
                <feature.icon className={cn('w-7 h-7', feature.color.replace('bg-', 'text-'))} />
              </div>

              {/* Content */}
              <h3 
                className="text-lg font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#FF6B35] transition-colors"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {feature.title}
              </h3>
              <p className="text-[#4A4A4A] text-sm leading-relaxed">
                {feature.description}
              </p>

              {/* Decorative Element */}
              <div className={cn(
                'w-12 h-1 rounded-full mt-4 transition-all duration-300',
                feature.color,
                'group-hover:w-full'
              )} />
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="bg-[#004E64] rounded-3xl p-8 md:p-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={stat.label} 
                className={cn(
                  'text-center',
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                )}
                style={{ transitionDelay: `${(index + 4) * 100}ms`, transition: 'all 0.6s ease-out' }}
              >
                <div 
                  className="text-4xl md:text-5xl font-bold text-white mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {stat.value}
                </div>
                <div className="text-white/70 text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
